import { useState, useEffect, useCallback } from 'react';

export interface WalletTransaction {
  id: string;
  type: 'faucet' | 'bet_placed' | 'bet_won';
  amount: number;
  label: string;
  timestamp: string;
}

const WALLET_STORAGE_KEY = 'predictx_wallet_balance_v1';
const TRANSACTIONS_KEY = 'predictx_wallet_txs_v1';
const INITIAL_BALANCE = 5000;

const WALLET_EVENT = 'predictx_wallet_update';

function getStoredBalance(): number {
  try {
    const raw = localStorage.getItem(WALLET_STORAGE_KEY);
    if (raw !== null) {
      const parsed = Number(raw);
      if (!Number.isNaN(parsed)) return parsed;
    }
  } catch {
    // fallback
  }
  return INITIAL_BALANCE;
}

function getStoredTransactions(): WalletTransaction[] {
  try {
    const raw = localStorage.getItem(TRANSACTIONS_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch {
    // fallback
  }
  return [
    {
      id: 'tx-welcome',
      type: 'faucet',
      amount: INITIAL_BALANCE,
      label: 'Welcome Bonus Credited',
      timestamp: new Date().toISOString(),
    },
  ];
}

export function useWallet() {
  const [balance, setBalance] = useState<number>(getStoredBalance);
  const [transactions, setTransactions] = useState<WalletTransaction[]>(getStoredTransactions);

  const syncState = useCallback(() => {
    setBalance(getStoredBalance());
    setTransactions(getStoredTransactions());
  }, []);

  useEffect(() => {
    window.addEventListener(WALLET_EVENT, syncState);
    return () => window.removeEventListener(WALLET_EVENT, syncState);
  }, [syncState]);

  const updateWallet = useCallback((newBalance: number, newTx?: WalletTransaction) => {
    localStorage.setItem(WALLET_STORAGE_KEY, String(newBalance));
    if (newTx) {
      const currentTxs = getStoredTransactions();
      const updated = [newTx, ...currentTxs].slice(0, 50);
      localStorage.setItem(TRANSACTIONS_KEY, JSON.stringify(updated));
    }
    window.dispatchEvent(new Event(WALLET_EVENT));
  }, []);

  const claimFaucet = useCallback((amount: number = 1000): { success: boolean; amount: number } => {
    const current = getStoredBalance();
    const next = current + amount;
    const tx: WalletTransaction = {
      id: `tx-${Date.now()}`,
      type: 'faucet',
      amount,
      label: 'Daily Faucet Claimed',
      timestamp: new Date().toISOString(),
    };
    updateWallet(next, tx);
    return { success: true, amount };
  }, [updateWallet]);

  const placeBet = useCallback((amount: number, marketTitle: string): boolean => {
    const current = getStoredBalance();
    if (current < amount) return false;
    const next = current - amount;
    const tx: WalletTransaction = {
      id: `tx-${Date.now()}`,
      type: 'bet_placed',
      amount: -amount,
      label: `Placed prediction on: ${marketTitle.slice(0, 24)}...`,
      timestamp: new Date().toISOString(),
    };
    updateWallet(next, tx);
    return true;
  }, [updateWallet]);

  const creditWin = useCallback((amount: number, label: string) => {
    const current = getStoredBalance();
    const next = current + amount;
    const tx: WalletTransaction = {
      id: `tx-${Date.now()}`,
      type: 'bet_won',
      amount,
      label: `Won: ${label}`,
      timestamp: new Date().toISOString(),
    };
    updateWallet(next, tx);
  }, [updateWallet]);

  return {
    balance,
    transactions,
    claimFaucet,
    placeBet,
    creditWin,
    canAfford: (amount: number) => balance >= amount,
  };
}
